import { type FC, Suspense, useCallback, useState, useEffect } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import {
    trackEvent,
    type MethodTypeValues,
    eventNameConstants,
    methodTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import serverVars from 'server-vars';
import { HeartPill } from 'dibs-nft-heart-pill/exports/HeartPill';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider';
import Twitter from 'dibs-icons/exports/legacy/Twitter';
import { Link } from 'dibs-elements/exports/Link';
import { twitterShare } from 'dibs-social-sharing/exports/shareActions';

import { type NftPdpSharingIcons_nftItem$key } from './__generated__/NftPdpSharingIcons_nftItem.graphql';
import { type NftPdpSharingIcons_viewer$key } from './__generated__/NftPdpSharingIcons_viewer.graphql';
import styles from './NftPdpSharingIcons.scss';
import layoutStyles from '../NftPdpRespLayout.scss';

const messages = defineMessages({
    twitterAriaLabel: {
        id: 'nft.pdp.sharing.twitter.label',
        defaultMessage: 'Share on Twitter',
    },
    twitterShareCopy: {
        id: 'nft.pdp.twitter.shareCopy',
        defaultMessage: 'Check out {name} by {creator}',
    },
    facebookAriaLabel: {
        id: 'nft.pdp.sharing.facebook.label',
        defaultMessage: 'Share on Facebook',
    },
    pinterestAriaLabel: {
        id: 'nft.pdp.sharing.pinterest.label',
        defaultMessage: 'Share on Pinterest',
    },
    pinterestShareCopy: {
        id: 'nft.pdp.sharing.pinterest.description',
        defaultMessage: '{name} | {description}',
    },
});

type Props = {
    nftItem: NftPdpSharingIcons_nftItem$key;
    viewer: NftPdpSharingIcons_viewer$key;
};

function trackSocialShare(label: MethodTypeValues): void {
    trackEvent({
        event: 'e_ga_event',
        category: 'share',
        action: 'NFT from PDP',
        label,
        noninteraction: 0,
        eventName: eventNameConstants.EVENT_SHARE,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_NFT_FROM_PDP,
        method: label,
    });
}

export const NftPdpSharingIcons: FC<Props> = ({ nftItem: nftItemRef, viewer: viewerRef }) => {
    const isMobile = serverVars.get('settings.isMobile');
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);
    const intl = useIntl();
    const viewer = useFragment(
        graphql`
            fragment NftPdpSharingIcons_viewer on Viewer {
                ...HeartPill_viewer
                ...NftHeartPillProvider_viewer
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment NftPdpSharingIcons_nftItem on NftItem {
                pdpUrl
                serviceId
                creatorAddress {
                    publicProfile {
                        profileName
                    }
                }
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        description
                        media {
                            webUrl
                        }
                    }
                }
                ...HeartPill_nftItem
            }
        `,
        nftItemRef
    );

    const { pdpUrl, metadata, creatorAddress } = nftItem || {};
    const { name } = metadata || {};

    const onTwitterClick = useCallback(() => {
        trackSocialShare(methodTypeConstants.METHOD_TYPE_TWITTER);
        twitterShare({
            newBrowserWindow: true,
            text: intl.formatMessage(messages.twitterShareCopy, {
                name,
                creator: creatorAddress?.publicProfile?.profileName,
            }),
            url: `${serverVars.get('localeOrigin')}${pdpUrl}`,
        });
    }, [intl, name, creatorAddress, pdpUrl]);

    const icons = [
        {
            ariaLabel: intl.formatMessage(messages.twitterAriaLabel),
            onClick: onTwitterClick,
            IconComponent: Twitter,
        },
    ];

    return (
        <div className={`${styles.iconsContainer} ${layoutStyles.socialSharing}`}>
            {isClient && !!nftItem.serviceId && (
                <Suspense fallback={''}>
                    <NftHeartPillProvider viewer={viewer} nftItemIds={[nftItem.serviceId]}>
                        <HeartPill
                            nftItem={nftItem}
                            viewer={viewer}
                            visible
                            containerClass={styles.heartPill}
                            isVertical={!isMobile}
                            shouldFillHeartOnHover
                            gaLabel="PDP"
                        />
                    </NftHeartPillProvider>
                </Suspense>
            )}
            {icons.map(({ ariaLabel, onClick, IconComponent }) => (
                <Link
                    key={ariaLabel}
                    className={styles.iconWrapper}
                    ariaLabel={ariaLabel}
                    onClick={onClick}
                >
                    <IconComponent className={styles.icon} />
                </Link>
            ))}
        </div>
    );
};
